import React from 'react';
import { Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Layout from './Layout';

/**
 * Les pages légales.
 *
 * Le contenu technique — ce qui est stocké, où, et qui d'autre y touche — est
 * tiré de ce que le code fait réellement, pas d'un modèle générique. Ce qui
 * relève de l'identité de l'éditeur est marqué : personne ne peut l'inventer.
 */
// Ce qui reste à faire relire par un avocat. Marqué, pour qu'on ne l'oublie
// pas sous un texte qui a l'air fini.
const A_RELIRE = '[clause à faire relire]';

const EDITEUR = {
  denomination: 'Expomis SARL-S',
  adresse: '69 rue de Hobscheid, 8422 Steinfort, Luxembourg',
  rcs: 'B274560',
  tva: 'LU35248949',
  contact: 'info@expomis.com',
};

function Article({ titre, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {titre}
      </Typography>
      <Stack spacing={1}>{children}</Stack>
    </Box>
  );
}

const P = ({ children }) => (
  <Typography variant="body2">{children}</Typography>
);

export function Confidentialite() {
  return (
    <Layout
      titre="Données et cookies"
      introduction="Ce que ce service enregistre, pourquoi, et qui d'autre y a accès."
    >
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Aucun traceur publicitaire, aucune mesure d'audience.
          </Typography>
          <Typography variant="body2">
            Ce service ne dépose pas de cookie de suivi. C'est pourquoi aucune
            bannière de consentement ne vous est présentée : il n'y a rien à
            consentir.
          </Typography>
        </Alert>

        <Article titre="Ce qui est conservé dans votre navigateur">
          <P>
            Deux jetons de connexion, dans le stockage local de votre
            navigateur. Ils prouvent que vous êtes bien connecté et évitent de
            redemander votre mot de passe à chaque page. Ils disparaissent à la
            déconnexion.
          </P>
          <P>
            La position et la taille de certaines fenêtres de travail, pour les
            retrouver comme vous les aviez laissées.
          </P>
          <P>
            Ces éléments sont strictement nécessaires au fonctionnement du
            service que vous avez demandé : ils ne servent ni à vous suivre, ni
            à vous profiler, et ne sont transmis à personne.
          </P>
        </Article>

        <Article titre="Ce que vous nous confiez">
          <P>
            Votre identité et vos coordonnées, votre contrat d'assurance, le
            récit de votre sinistre, vos dommages et leurs photos, l'inventaire
            de votre contenu, les documents que vous déposez, et les échanges
            avec votre assureur que vous consignez.
          </P>
          <P>
            Ces données servent à constituer et défendre votre réclamation.
            Elles ne sont accessibles qu'à vous, aux personnes à qui vous
            partagez explicitement le dossier, et à notre équipe lorsqu'une
            intervention technique l'exige.
          </P>
        </Article>

        <Article titre="Ce qui sort de nos serveurs, et ce qui n'en sort pas">
          <P>
            <strong>Vos documents ne quittent pas nos serveurs.</strong> La
            reconnaissance de texte sur vos scans et photos est effectuée
            localement.
          </P>
          <P>
            En revanche, lorsque vous demandez une analyse — relecture de votre
            déclaration, lecture d'un courrier, analyse de votre contrat — le
            <strong> texte concerné</strong> est transmis à OpenAI, notre
            prestataire d'intelligence artificielle, pour y être traité. Les
            photographies de dommages peuvent l'être également lorsque vous
            demandez leur analyse.
          </P>
          <P>
            Lors de la création d'un compte, Google reCAPTCHA vérifie que vous
            n'êtes pas un automate. Ce service est fourni par Google et relève
            de sa propre politique de confidentialité.
          </P>
        </Article>

        <Article titre="Combien de temps">
          <P>
            Vos dossiers sont conservés <strong>dix ans à compter de leur
            clôture</strong>, durée qui correspond aux délais de prescription
            applicables en matière d'assurance.
          </P>
          <P>
            Lorsqu'un partage est retiré, une copie figée du dossier reste
            accessible à la personne qui perd l'accès, afin qu'elle conserve la
            trace de son travail. Cette copie suit sa propre durée de
            conservation : elle survit à la suppression du dossier d'origine.
          </P>
        </Article>

        <Article titre="Vos droits">
          <P>
            Vous pouvez accéder à vos données, les corriger, en demander
            l'effacement, la limitation ou une copie portable, et vous opposer
            à certains traitements. Écrivez à{' '}
            <strong>{EDITEUR.contact}</strong>.
          </P>
          <P>
            Vous pouvez introduire une réclamation auprès de l'autorité de
            protection des données de votre pays — la CNPD au Luxembourg,
            l'Autorité de protection des données en Belgique.
          </P>
        </Article>

        <Article titre="Qui traite ces données">
          <P>
            <strong>{EDITEUR.denomination}</strong>, {EDITEUR.adresse}.
          </P>
          <P>
            Registre de commerce {EDITEUR.rcs} — TVA {EDITEUR.tva}.
          </P>
          <P>Contact : {EDITEUR.contact}</P>
        </Article>

        <Typography variant="caption" color="text.secondary">
          <Link to="/conditions">Conditions générales d'utilisation</Link>
        </Typography>
      </Paper>
    </Layout>
  );
}

export function Conditions() {
  return (
    <Layout
      titre="Conditions générales d'utilisation"
      introduction="Ce que ce service fait, ce qu'il ne fait pas, et ce qui reste de votre ressort."
    >
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Ce service ne remplace ni un expert, ni un avocat, ni votre
            assureur.
          </Typography>
          <Typography variant="body2">
            Il vous aide à constituer un dossier et à comprendre votre contrat.
            Les montants qu'il calcule sont des repères issus d'un barème de
            référence : ils n'engagent personne, et surtout pas votre
            compagnie.
          </Typography>
        </Alert>

        <Article titre="Objet">
          <P>
            Iziclai permet à un assuré, seul ou assisté de son intermédiaire,
            de déclarer un sinistre, de chiffrer ses dommages, de constituer sa
            réclamation et de suivre la réponse de son assureur.
          </P>
        </Article>

        <Article titre="Ce que les montants valent">
          <P>
            Les chiffrages reposent sur un barème de référence et sur les
            quantités que vous saisissez. Deux évaluations sérieuses du même
            dommage diffèrent couramment de dix pour cent : nos montants sont
            donc présentés sous forme de fourchette, et vous restez libre de
            réclamer sur la base du devis de votre entrepreneur.
          </P>
          <P>
            Aucun montant affiché ne constitue une offre, un engagement de
            votre assureur, ni une promesse d'indemnisation.
          </P>
        </Article>

        <Article titre="Ce que l'analyse automatique fait, et ne fait pas">
          <P>
            Certaines fonctions s'appuient sur une intelligence artificielle :
            relecture de votre déclaration, lecture d'un courrier reçu, mise en
            regard de votre contrat. Elle signale, propose et reformule — elle
            ne décide jamais à votre place.
          </P>
          <P>
            Rien de ce qu'elle produit n'est enregistré sans votre validation.
            Elle peut se tromper : relisez avant de retenir.
          </P>
        </Article>

        <Article titre="Vos obligations">
          <P>
            Vous déclarez des faits exacts et fournissez des documents
            authentiques. Vous restez seul responsable de ce que vous adressez
            à votre assureur, des délais de déclaration prévus à votre contrat,
            et de la conservation de vos preuves d'envoi.
          </P>
          <P>
            Le service vous signale ce qui manque et vous rappelle les délais
            qu'il connaît, sans s'y substituer.
          </P>
        </Article>

        <Article titre="Partage d'un dossier">
          <P>
            Vous pouvez donner accès à votre dossier à un tiers, en lecture ou
            en modification. Vous pouvez retirer cet accès à tout moment. La
            personne qui le perd conserve une copie figée de l'état du dossier
            au moment du retrait.
          </P>
        </Article>

        <Article titre="Disponibilité et responsabilité">
          <P>
            Le service est fourni sans garantie d'ininterruption. Les
            interruptions nécessaires à la maintenance sont annoncées quand
            elles sont prévisibles.
          </P>
          <P>
            {A_RELIRE} — étendue de la responsabilité de l'éditeur. En tout
            état de cause, aucune stipulation ne limite la responsabilité en
            cas de faute lourde ou de dol, ni ne prive un consommateur des
            protections impératives de son droit national.
          </P>
        </Article>

        <Article titre="Droit applicable et juridiction">
          <P>
            Les présentes conditions sont régies par le <strong>droit
            luxembourgeois</strong>. Tout litige relève des juridictions de
            Luxembourg-Ville.
          </P>
          <P>
            <strong>Réserve expresse en faveur du consommateur.</strong> Si
            vous utilisez ce service en dehors de votre activité
            professionnelle, cette clause ne vous prive d'aucun droit : vous
            conservez la faculté de saisir la juridiction de votre domicile, et
            les dispositions impératives du droit de votre pays de résidence
            continuent de s'appliquer. Un assuré résidant en Belgique peut donc
            agir devant son propre tribunal et se prévaloir du droit belge de
            la consommation.
          </P>
        </Article>

        <Article titre="Éditeur">
          <P>
            <strong>{EDITEUR.denomination}</strong>, {EDITEUR.adresse}.
          </P>
          <P>
            Registre de commerce {EDITEUR.rcs} — TVA {EDITEUR.tva}.
          </P>
          <P>Contact : {EDITEUR.contact}</P>
        </Article>

        <Typography variant="caption" color="text.secondary">
          <Link to="/confidentialite">Données et cookies</Link>
        </Typography>
      </Paper>
    </Layout>
  );
}
